import {FC, useCallback, useEffect, useReducer, useRef} from "react"
import {Snippet} from "../../model/Snippet"
import {ViewMode} from "../../model/ViewMode"
import {multilineEditorService} from "../../service/multilineEditorService"
import {TextAreaInput} from "./TextAreaInput"
import {LineNumberDisplay} from "./LineNumberDisplay"
import {AutoCompleteSuggestions} from "./AutoCompleteSuggestions"
import {SnippetControls} from "./SnippetControls"
import {useAutoComplete} from "./hooks/useAutoComplete"
import {useKeyboardShortcuts} from "./hooks/useKeyboardShortcuts"
import {useAutoSave} from "./hooks/useAutoSave"
import {useLineNumbers} from "./hooks/useLineNumbers"
import {editorReducer} from "./editorReducer"
import {MultilineEditorProps, TextAreaRefs} from "./types"
import './MultilineEditor.less'

export const MultilineEditor: FC<MultilineEditorProps> = ({
    value,
    setValue,
    settings,
    className,
    style,
    disabled,
    snippets,
    words,
    onCancel,
    onConfirm,
    onBlur,
    onSnippetUsed,
    onCursorChange,
}) => {
    // Initialize state with reducer
    const [state, dispatch] = useReducer(editorReducer, {
        allSnippets: false,
        viewMode: ViewMode.NORMAL,
        lastSave: 0,
        selStart: 0,
        selEnd: 0,
        suggestions: [],
        showSuggestions: false,
        activeIdx: 0,
        autoComplete: false,
    })

    // Create refs
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const suggestionsRef = useRef<HTMLUListElement>(null)
    const lineNumbersRef = useRef<HTMLDivElement>(null)

    const refs: TextAreaRefs = {
        textAreaRef,
        suggestionsRef,
        lineNumbersRef
    }

    // Set up auto save hook
    useAutoSave({
        enabled: !!settings.autoSave,
        lastSave: state.lastSave,
        setLastSave: (time) => dispatch({ type: 'SET_LAST_SAVE', payload: time }),
        onConfirm
    })

    // Set up line numbers synchronization
    useLineNumbers({
        enabled: !!settings.lineNumbers,
        textAreaRef,
        lineNumbersRef
    })

    // Set up auto-complete functionality
    useAutoComplete({
        state,
        value,
        dispatch,
        words
    })

    // Set up keyboard shortcuts
    const { handleKeyDown } = useKeyboardShortcuts({
        state,
        value,
        setValue,
        onConfirm,
        onCancel,
        dispatch,
        snippets,
        onSnippetUsed,
        textAreaRef,
        allowFullScreen: settings.allowFullScreen,
        replaceWord: (suggestion: string) => {
            const word = getCurrentWord()
            const wordStart = state.selStart - word.length
            const newText = value.slice(0, wordStart) + suggestion + value.slice(state.selStart)
            setValue(newText)
            const newCaret = wordStart + suggestion.length
            dispatch({ type: 'SET_SELECTION', payload: { start: newCaret, end: newCaret } })
            dispatch({ type: 'HIDE_SUGGESTIONS' })
            // keep focus and cursor in place
            setTimeout(() => {
                textAreaRef.current?.setSelectionRange(newCaret, newCaret)
            }, 0)
        }
    })

    // Handle textarea value change
    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(event.target.value)
    }

    // Handle textarea blur
    const handleBlur = () => {
        if (onBlur) {
            onBlur(value)
        }
        setTimeout(() => dispatch({ type: 'HIDE_SUGGESTIONS' }), 150)
    }

    // Handle textarea selection
    const handleSelect = () => {
        const el = textAreaRef.current
        if (!el) return
        dispatch({ 
            type: 'SET_SELECTION', 
            payload: { 
                start: el.selectionStart, 
                end: el.selectionEnd 
            } 
        })
        if (onCursorChange) {
            const beforeCaret = value.slice(0, el.selectionStart)
            const rows = beforeCaret.split('\n')
            const row = rows.length - 1
            const col = rows[rows.length - 1].length
            onCursorChange({ row, col })
        }
    }

    // Get current word at cursor
    const getCurrentWord = (): string => {
        if (value) {
            const beforeCaret = value.slice(0, state.selStart)
            const match = beforeCaret.match(/(\w+)$/)
            return match ? match[1] : ""
        } else {
            return ""
        }
    }

    // Handle suggestion click
    const handleSuggestionClick = useCallback((idx: number) => {
        return (e: React.MouseEvent<HTMLLIElement>) => {
            e.preventDefault()
            const suggestion = state.suggestions[idx]
            const word = getCurrentWord()
            const wordStart = state.selStart - word.length
            const newText = value.slice(0, wordStart) + suggestion + value.slice(state.selStart)
            setValue(newText)
            const newCaret = wordStart + suggestion.length
            dispatch({ type: 'SET_SELECTION', payload: { start: newCaret, end: newCaret } })
            dispatch({ type: 'HIDE_SUGGESTIONS' })
            // keep focus and cursor in place
            setTimeout(() => {
                textAreaRef.current?.setSelectionRange(newCaret, newCaret)
            }, 0)
        }
    }, [state.suggestions, state.selStart, value, setValue])

    // Handle adding a snippet
    const handleAddSnippet = useCallback((snippet: Snippet) => {
        return (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault()
            event.stopPropagation()
            const textarea = textAreaRef.current
            if (!textarea) return

            const start = textarea.selectionStart
            const end = textarea.selectionEnd

            const before = value.substring(0, start)
            const after = value.substring(end)

            const newValue = before + snippet.content + after
            if (onSnippetUsed) {
                onSnippetUsed(newValue)
            } else {
                setValue(newValue)
            }

            requestAnimationFrame(() => {
                textarea.focus()
                const cursorPosition = start + snippet.content.length
                textarea.setSelectionRange(cursorPosition, cursorPosition)
            })
        }
    }, [value, setValue, onSnippetUsed])

    // Handle toggling snippets
    const handleToggleSnippets = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        dispatch({ type: 'SET_ALL_SNIPPETS', payload: !state.allSnippets })
    }, [state.allSnippets])

    // Handle toggling fullscreen
    const handleToggleFullScreen = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        if (settings.allowFullScreen === false) {
            return
        }
        const nextMode = multilineEditorService.getNextMode(state.viewMode)
        dispatch({ type: 'SET_VIEW_MODE', payload: nextMode })
    }, [state.viewMode, settings.allowFullScreen])

    // Handle toggling autocomplete
    const handleToggleAutocomplete = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        dispatch({ type: 'SET_AUTO_COMPLETE', payload: !state.autoComplete })
    }, [state.autoComplete])

    // Auto-resize effect
    useEffect(() => {
        if (!!settings.autoResize && textAreaRef && textAreaRef.current) {
            textAreaRef.current.style.height = '0px'
            const scrollHeight = textAreaRef.current.scrollHeight
            textAreaRef.current.style.height = scrollHeight.toString() + "px"
            if (style) {
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                textAreaRef.current.style.backgroundColor = style.backgroundColor
                // @ts-ignore
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                textAreaRef.current.style.border = style.border
            }
        }
    }, [textAreaRef, value, style, settings.autoResize])

    // Select all effect
    useEffect(() => {
        if (settings.selectAll && textAreaRef && textAreaRef.current) {
            textAreaRef.current.select()
        }
    }, [textAreaRef, settings.selectAll])

    // Determine view mode classes and title
    const allowFullScreen = settings.allowFullScreen !== false
    const classes = ['multilineEditor']
    if (allowFullScreen && state.viewMode === ViewMode.FULL_SCREEN) {
        classes.push("fullScreen")
    } else if (allowFullScreen && state.viewMode === ViewMode.RIGHT_PANEL) {
        classes.push("rightPanel")
    }

    let viewModeTitle = 'Full screen (F4)'
    if (state.viewMode === ViewMode.FULL_SCREEN) {
        viewModeTitle = 'To right panel (F4)'
    } else if (state.viewMode === ViewMode.RIGHT_PANEL) {
        viewModeTitle = 'Minimize (F4)'
    }

    return (
        <div className={classes.join(' ')}>
            <LineNumberDisplay 
                value={value} 
                isVisible={!!settings.lineNumbers} 
                containerRef={lineNumbersRef}
            />
            <TextAreaInput
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                autoFocus={settings.autoFocus}
                className={className}
                onBlur={handleBlur}
                onSelect={handleSelect}
                disabled={disabled}
                refs={refs}
            />
            <AutoCompleteSuggestions
                suggestions={state.suggestions}
                showSuggestions={state.showSuggestions}
                activeIdx={state.activeIdx}
                onSuggestionClick={handleSuggestionClick}
                suggestionsRef={suggestionsRef}
            />
            {snippets && snippets.length > 0 && (
                <SnippetControls
                    snippets={snippets}
                    allSnippets={state.allSnippets}
                    autoComplete={state.autoComplete}
                    viewMode={state.viewMode}
                    onAddSnippet={handleAddSnippet}
                    onToggleSnippets={handleToggleSnippets}
                    onToggleFullScreen={handleToggleFullScreen}
                    onToggleAutocomplete={handleToggleAutocomplete}
                    viewModeTitle={viewModeTitle}
                />
            )}
        </div>
    )
}
